import { useEffect, useState } from "react";
import { Download, Share, Plus, X, Smartphone } from "lucide-react";
import { onPwaState, promptInstall, isIOS, isStandalone } from "@/lib/pwa";
import { useI18n } from "@/context/I18nContext";

const DISMISS_KEY = "uc_pwa_dismissed_at";
const REMIND_MS = 1000 * 60 * 60 * 24 * 7; // 7 gün

function wasRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    return Date.now() - ts < REMIND_MS;
  } catch { return false; }
}

/**
 * Smart PWA install prompt.
 * - Chrome/Edge/Android: uses captured beforeinstallprompt.
 * - iOS Safari: shows guided "Paylaş → Ana Ekrana Ekle" instructions.
 * - Auto-hidden if already installed, dismissed <7 days ago, or unsupported.
 */
export default function PWAInstallPrompt() {
  const { t } = useI18n();
  const [state, setState] = useState({ canPrompt: false, standalone: false, ios: false });
  const [dismissed, setDismissed] = useState(wasRecentlyDismissed());
  const [showIOSSheet, setShowIOSSheet] = useState(false);

  useEffect(() => onPwaState(setState), []);

  // Delay first appearance a bit so the page can settle in.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 2500);
    return () => clearTimeout(t);
  }, []);

  if (!ready) return null;
  if (isStandalone() || state.standalone) return null;
  if (dismissed) return null;

  const iosMode = isIOS() || state.ios;
  const showBanner = state.canPrompt || iosMode;
  if (!showBanner) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setDismissed(true);
    setShowIOSSheet(false);
  };

  const handleInstall = async () => {
    if (iosMode && !state.canPrompt) {
      setShowIOSSheet(true);
      return;
    }
    const res = await promptInstall();
    if (res && res.outcome === "dismissed") handleDismiss();
  };

  return (
    <>
      {/* Floating premium install banner (mobile-first, desktop hidden if not eligible) */}
      <div
        className="fixed left-1/2 -translate-x-1/2 z-[80] w-[calc(100%-1.5rem)] max-w-md"
        style={{ bottom: "max(1rem, env(safe-area-inset-bottom))" }}
        data-testid="pwa-install-banner"
      >
        <div className="relative overflow-hidden border border-[--gold]/40 bg-[#080808]/95 backdrop-blur-xl shadow-[0_25px_60px_-15px_rgba(212,175,55,0.35)]">
          {/* Gold glow accent */}
          <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full pointer-events-none"
               style={{ background: "radial-gradient(circle, rgba(212,175,55,0.22), transparent 70%)" }} />
          <div className="relative p-4 sm:p-5 flex items-start gap-3 sm:gap-4">
            <div className="shrink-0 w-11 h-11 sm:w-12 sm:h-12 border border-[--gold]/40 flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.14), transparent)" }}>
              <Smartphone size={20} style={{ color: "var(--gold)" }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono tracking-[0.28em] uppercase" style={{ color: "var(--gold)" }}>
                {t("pwa.badge")}
              </div>
              <div className="text-white text-sm sm:text-base font-medium mt-1 leading-tight">
                {t("pwa.title")}
              </div>
              <div className="text-neutral-400 text-[11px] sm:text-xs leading-relaxed mt-1">
                {t("pwa.subtitle")}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={handleInstall}
                  className="btn-primary-gold inline-flex items-center gap-1.5 px-3.5 py-2 text-xs sm:text-[13px]"
                  data-testid="pwa-install-btn"
                >
                  <Download size={13} />
                  {iosMode && !state.canPrompt ? t("pwa.ios_cta") : t("pwa.install_cta")}
                </button>
                <button
                  onClick={handleDismiss}
                  className="text-[11px] sm:text-xs text-neutral-500 hover:text-neutral-300 px-2.5 py-2"
                  data-testid="pwa-dismiss-btn"
                >
                  {t("pwa.later")}
                </button>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              aria-label="close"
              className="text-neutral-500 hover:text-white shrink-0 -mt-1 -mr-1 p-1"
              data-testid="pwa-close-btn"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* iOS bottom-sheet instructions */}
      {showIOSSheet && (
        <div
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setShowIOSSheet(false)}
          data-testid="pwa-ios-sheet"
        >
          <div
            className="w-full max-w-md border border-[--gold]/40 bg-[#0a0a0a] p-6 sm:p-7 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowIOSSheet(false)}
              aria-label="close"
              className="absolute top-3 right-3 text-neutral-500 hover:text-white p-1"
            >
              <X size={18} />
            </button>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase" style={{ color: "var(--gold)" }}>
              iOS · Safari
            </div>
            <h3 className="font-display text-xl sm:text-2xl mt-2 text-white">
              {t("pwa.ios_title")}
            </h3>
            <ol className="mt-5 space-y-4 text-sm text-neutral-300">
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 border border-[--gold]/40 flex items-center justify-center text-[11px] font-mono" style={{ color: "var(--gold)" }}>1</span>
                <span className="flex items-center gap-2">
                  {t("pwa.ios_step1")}
                  <Share size={16} style={{ color: "var(--gold)" }} />
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 border border-[--gold]/40 flex items-center justify-center text-[11px] font-mono" style={{ color: "var(--gold)" }}>2</span>
                <span className="flex items-center gap-2">
                  {t("pwa.ios_step2")}
                  <Plus size={16} style={{ color: "var(--gold)" }} />
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 border border-[--gold]/40 flex items-center justify-center text-[11px] font-mono" style={{ color: "var(--gold)" }}>3</span>
                <span>{t("pwa.ios_step3")}</span>
              </li>
            </ol>
            <div className="mt-6 pt-4 border-t border-white/8 text-[11px] font-mono tracking-wider text-neutral-500">
              {t("pwa.ios_hint")}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
